import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender, type Header, type Table } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown, GripHorizontal } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

interface DraggableColumnHeaderProps<T> {
  header: Header<T, unknown>
  table: Table<T>
}

export function DraggableColumnHeader<T>({
  header,
}: DraggableColumnHeaderProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getSortIcon = (isSorted: false | 'asc' | 'desc') => {
    if (isSorted === 'asc') return <ArrowUp className='w-4 h-4' />
    if (isSorted === 'desc') return <ArrowDown className='w-4 h-4' />
    return <ArrowUpDown className='w-4 h-4' />
  }

  return (
    <th
      ref={setNodeRef}
      style={{
        ...style,
        width: header.getSize(),
        minWidth: header.getSize(),
      }}
      className='relative group bg-slate-100 dark:bg-slate-800 p-2 text-left font-normal text-slate-900 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors'
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className='cursor-move opacity-0 group-hover:opacity-100 transition-opacity'
          >
            <GripHorizontal className='w-4 h-4 text-slate-400' />
          </div>

          {/* Header content */}
          <div className='flex-1 min-w-0'>
            {header.isPlaceholder ? null : (
              <div className='flex items-center gap-2'>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                {header.column.getCanSort() && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-6 w-6 p-0 hover:bg-slate-200 dark:hover:bg-slate-600'
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {getSortIcon(header.column.getIsSorted())}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resize handle */}
        {header.column.getCanResize() && (
          // biome-ignore lint/a11y/noStaticElementInteractions: mouse-only resize handle
          <div
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className='absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity'
            style={{
              userSelect: 'none',
              touchAction: 'none',
            }}
          />
        )}
      </div>
    </th>
  )
}
